import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams } from 'react-router-dom';
import { kioskAttendCheck, kioskAttendSelect, loadClubInfoByID, kioskStaffCall } from '../../lib/api/kiosk';
import { decodeClubID } from '../../lib/clubIdCipher';
import { MdBackspace, MdFullscreen, MdFullscreenExit } from 'react-icons/md';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;


const PageWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100vh;
  display: flex;
  background: #FFFFFF;
  font-family: 'Noto Sans KR', sans-serif;
  overflow: hidden;
  user-select: none;
  position: fixed;
  top: 0;
  left: 0;
`;

const LeftSection = styled.div`
  width: clamp(400px, 50vw, 700px);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 5vh 3vw;
  background: #FFFFFF;
  border-right: 1px solid #F0F0F0;
  position: relative;
`;


const LeftContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const GymName = styled.h1`
  font-size: clamp(30px, 3.8vw, 56px);
  font-weight: 600;
  color: #111111;
  margin-bottom: clamp(16px, 2.5vh, 28px);
  word-break: keep-all;
  letter-spacing: -0.5px;
`;

const Instruction = styled.p`
  font-size: clamp(14px, 1.2vw, 18px);
  color: #AAAAAA;
  text-align: center;
  line-height: 1.9;
  word-break: keep-all;
  font-weight: 400;
`;

const LeftBottom = styled.div`
  position: absolute;
  bottom: clamp(40px, 6vh, 72px);
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(16px, 2vh, 28px);
`;

const StaffCallButton = styled.button`
  padding: clamp(10px, 1.2vh, 16px) clamp(28px, 3vw, 48px);
  border: 1px solid #E8E8E8;
  border-radius: 100px;
  background: #FFFFFF;
  color: #999999;
  font-size: clamp(13px, 1.1vw, 16px);
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #CCCCCC;
    color: #666666;
  }

  &:active {
    transform: scale(0.97);
  }

  &:disabled {
    border-color: #CCCCCC;
    color: #666666;
    cursor: default;
  }
`;

const LogoImg = styled.img`
  height: clamp(18px, 2.2vh, 28px);
  object-fit: contain;
  opacity: 0.9;
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #FFFFFF;
  position: relative;
`;

const FullscreenButton = styled.button`
  position: absolute;
  top: 1.5vh;
  left: 1.5vw;
  z-index: 10;
  width: clamp(34px, 3.5vh, 48px);
  height: clamp(34px, 3.5vh, 48px);
  border: none;
  border-radius: 10px;
  background: #F5F5F5;
  color: #BBBBBB;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: clamp(20px, 2.5vh, 30px);
  transition: all 0.2s ease;

  &:hover {
    background: #EBEBEB;
    color: #888888;
  }
`;

const NumpadCard = styled.div`
  background: #FFFFFF;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const PhoneDisplay = styled.div`
  padding: clamp(28px, 4vh, 52px) clamp(20px, 2vw, 32px);
  text-align: center;
  border-bottom: 1px solid #E5E5E5;
`;

const PhoneLabel = styled.div`
  font-size: clamp(12px, 1vw, 15px);
  color: #555555;
  margin-bottom: clamp(8px, 1.2vh, 16px);
  font-weight: 600;
`;

const PhoneNumber = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: clamp(24px, 4vw, 52px);
  font-size: clamp(42px, 5.5vw, 72px);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

const PhoneDigit = styled.span`
  display: inline-block;
  width: 1ch;
  text-align: center;
  color: ${props => props.filled ? '#222222' : '#D5D5D5'};
`;

const NumpadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(4, 1fr);
  width: 100%;
  flex: 1;
`;

const GridCell = styled.button`
  width: 100%;
  border: none;
  border-right: 1px solid #ECECEC;
  border-bottom: 1px solid #ECECEC;
  background: #FFFFFF;
  font-size: clamp(24px, 2.8vw, 40px);
  font-weight: 600;
  color: #1A1A1A;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background 0.1s ease;

  &:nth-child(3n) {
    border-right: none;
  }

  &:nth-child(n+10) {
    border-bottom: none;
  }

  &:hover {
    background: #F7F8FA;
  }

  &:active {
    background: #EEF0F4;
  }
`;

const BackspaceCell = styled(GridCell)`
  color: #AAAAAA;
  font-size: clamp(22px, 2.4vw, 34px);
`;

const SubmitButton = styled.button`
  width: 100%;
  height: clamp(60px, 10vh, 100px);
  flex-shrink: 0;
  border: none;
  border-top: 1px solid #ECECEC;
  background: ${props => props.disabled ? '#D4D9E3' : '#2E8BFF'};
  color: #FFFFFF;
  font-size: clamp(20px, 2.2vw, 32px);
  font-weight: 600;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background 0.15s ease;

  &:hover {
    ${props => !props.disabled && 'background: #1A7AEF;'}
  }

  &:active {
    ${props => !props.disabled && 'background: #0D6ADF;'}
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${overlayFadeIn} 0.2s ease;
`;

const ResultCard = styled.div`
  background: #FFFFFF;
  border-radius: 24px;
  padding: 5vh 4vw;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-width: 90vw;
  max-height: 85vh;
  overflow-y: auto;
  width: clamp(440px, 50vw, 700px);
  position: relative;
  animation: ${fadeIn} 0.25s ease;
`;

const CloseButton = styled.button`
  position: absolute;
  top: clamp(14px, 1.8vh, 22px);
  right: clamp(14px, 1.4vw, 22px);
  width: clamp(34px, 3.5vh, 46px);
  height: clamp(34px, 3.5vh, 46px);
  border: none;
  border-radius: 50%;
  background: #F0F0F0;
  color: #AAAAAA;
  font-size: clamp(15px, 1.6vw, 20px);
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    background: #E4E4E4;
    color: #666666;
  }
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(12px, 1.5vw, 20px);
`;

const ResultIcon = styled.div`
  width: clamp(48px, 6vh, 72px);
  height: clamp(48px, 6vh, 72px);
  border-radius: 50%;
  background: ${props => props.success ? 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)' : 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)'};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: clamp(24px, 3vh, 40px);
  flex-shrink: 0;
`;

const ResultTitle = styled.h2`
  font-size: clamp(22px, 2.8vw, 36px);
  font-weight: 700;
  color: ${props => props.success ? '#2E7D32' : '#C62828'};
  margin: 0;
`;

const ResultMessage = styled.p`
  font-size: clamp(14px, 1.5vw, 22px);
  color: #8C8C8C;
  line-height: 1.5;
  margin-top: clamp(8px, 1.2vh, 14px);
`;

const InfoSection = styled.div`
  margin-top: clamp(20px, 3vh, 32px);
  text-align: left;
  width: 100%;
  max-height: 40vh;
  overflow-y: auto;
`;

const InfoLabel = styled.div`
  font-size: clamp(11px, 1vw, 14px);
  font-weight: 600;
  color: #AAAAAA;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: clamp(6px, 1vh, 10px);
  margin-top: clamp(14px, 1.8vh, 22px);

  &:first-child {
    margin-top: 0;
  }
`;

const InfoCard = styled.div`
  background: #F8F9FB;
  border-radius: 12px;
  padding: clamp(12px, 1.8vh, 20px) clamp(14px, 1.4vw, 22px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #F0F0F0;

  & + & {
    margin-top: clamp(6px, 0.8vh, 10px);
  }
`;

const InfoName = styled.span`
  font-size: clamp(13px, 1.3vw, 18px);
  font-weight: 600;
  color: #2C2C2C;
`;

const InfoDate = styled.span`
  font-size: clamp(11px, 1vw, 15px);
  color: #AAAAAA;
`;

const InfoEmpty = styled.p`
  font-size: clamp(12px, 1.1vw, 16px);
  color: #C4C4C4;
  text-align: center;
  padding: clamp(8px, 1vh, 14px) 0;
`;

const CountdownText = styled.p`
  font-size: clamp(11px, 1vw, 14px);
  color: #CCCCCC;
  font-weight: 400;
  margin-top: clamp(20px, 3vh, 32px);
`;

const RemainingDaysWrap = styled.div`
  margin-top: clamp(20px, 3vh, 32px);
  text-align: center;
`;

const RemainingDaysNumber = styled.span`
  font-size: clamp(36px, 4vw, 56px);
  font-weight: 700;
  color: #2E8BFF;
`;

const RemainingDaysLabel = styled.span`
  font-size: clamp(16px, 1.6vw, 24px);
  font-weight: 600;
  color: #2E8BFF;
  margin-left: 4px;
`;

const SelectCard = styled.div`
  background: #FFFFFF;
  border-radius: 24px;
  padding: 4vh 3vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  width: clamp(420px, 45vw, 600px);
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.25s ease;
`;

const SelectTitle = styled.h2`
  font-size: clamp(18px, 2vw, 28px);
  font-weight: 700;
  color: #141414;
  margin-bottom: 1vh;
  text-align: center;
`;

const SelectSubtitle = styled.p`
  font-size: clamp(12px, 1.2vw, 18px);
  color: #AAAAAA;
  margin-bottom: 3vh;
  text-align: center;
`;

const MemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1.2vh, 16px);
  margin-bottom: 3vh;
  overflow-y: auto;
  flex: 1;
`;

const MemberItem = styled.button`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: clamp(14px, 2vh, 28px) clamp(16px, 1.5vw, 28px);
  border: 1px solid #EEEEEE;
  border-radius: 14px;
  background: #FFFFFF;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    border-color: #3B82F6;
    background: #F5F9FF;
    box-shadow: 0 2px 12px rgba(59, 130, 246, 0.1);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const MemberName = styled.span`
  font-size: clamp(16px, 1.6vw, 24px);
  font-weight: 600;
  color: #2C2C2C;
`;

const MemberPhone = styled.span`
  font-size: clamp(12px, 1.2vw, 18px);
  color: #AAAAAA;
  letter-spacing: 0.5px;
`;

const CancelButton = styled.button`
  width: 100%;
  height: clamp(40px, 5vh, 60px);
  border: 1px solid #EEEEEE;
  border-radius: 14px;
  background: #FFFFFF;
  color: #8C8C8C;
  font-size: clamp(14px, 1.4vw, 20px);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #F8F8F8;
  }
`;

const successAudio = new Audio('/sounds/success.mp3');
const failAudio = new Audio('/sounds/fail.mp3');

const playSuccessSound = () => {
  try { successAudio.currentTime = 0; successAudio.play(); } catch (e) {}
};

const playFailSound = () => {
  try { failAudio.currentTime = 0; failAudio.play(); } catch (e) {}
};

let audioCtx = null;
const getAudioCtx = () => {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new AudioContext();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
};

const playKeySound = () => {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 1200;
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.08);
  } catch (e) {}
};

const playStaffCallSound = () => {
  try {
    const ctx = getAudioCtx();
    [880, 1108.73].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.12);
      gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + i * 0.12 + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.3);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.12);
      osc.stop(ctx.currentTime + i * 0.12 + 0.3);
    });
  } catch (e) {}
};

const formatDate = (unix) => {
  if (!unix) return '';
  const d = new Date(unix * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

const formatPhone = (phone) => {
  if (!phone) return phone;
  const nums = phone.replace(/[^0-9]/g, '');
  if (nums.length === 11) {
    return `${nums.slice(0, 3)}-${nums.slice(3, 5)}**-${nums.slice(7)}`;
  }
  if (nums.length === 10) {
    return `${nums.slice(0, 3)}-${nums.slice(3, 4)}**-${nums.slice(6)}`;
  }
  return phone;
};

const calcRemainingDays = (memberships) => {
  if (!memberships || memberships.length === 0) return null;
  const now = Math.floor(Date.now() / 1000);
  const sorted = [...memberships].sort((a, b) => a.startTime - b.startTime);

  // 현재 활성 회원권들의 최대 endTime으로 시작
  let mergedEnd = 0;
  for (const m of sorted) {
    if (m.startTime <= now && m.endTime >= now && m.endTime > mergedEnd) {
      mergedEnd = m.endTime;
    }
  }
  if (mergedEnd === 0) return null;

  // 연속되는 미래 회원권 병합 (1일 이내 gap은 연속으로 간주)
  let changed = true;
  while (changed) {
    changed = false;
    for (const m of sorted) {
      if (m.startTime <= mergedEnd + 86400 && m.endTime > mergedEnd) {
        mergedEnd = m.endTime;
        changed = true;
      }
    }
  }

  const diffSec = mergedEnd - now;
  if (diffSec <= 0) return 0;
  return Math.ceil(diffSec / 86400);
};

const KioskAttendCheckPage = () => {
  const { code } = useParams();
  const clubID = decodeClubID(code);
  const [digits, setDigits] = useState('');
  const [clubName, setClubName] = useState('');
  const [result, setResult] = useState(null);
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [resultCountdown, setResultCountdown] = useState(0);
  const [staffCalled, setStaffCalled] = useState(false);
  const [staffCountdown, setStaffCountdown] = useState(0);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      try {
        wakeLock = await navigator.wakeLock.request('screen');
        wakeLock.addEventListener('release', () => { wakeLock = null; });
      } catch (e) {}
    };

    requestWakeLock();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') requestWakeLock();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (wakeLock) wakeLock.release();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const loadClub = async () => {
      try {
        const res = await loadClubInfoByID({ clubID });
        if (res.data && res.data.result === 1 && res.data.info) {
          setClubName(res.data.info.name || '');
        }
      } catch (e) {
        console.log('club info load failed:', e);
      }
    };
    if (clubID) loadClub();
  }, [clubID]);

  useEffect(() => {
    if (result) {
      if (result.type === 'success') playSuccessSound();
      else playFailSound();
      const seconds = result.type === 'success' ? 10 : 3;
      setResultCountdown(seconds);
      const interval = setInterval(() => {
        setResultCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setResult(null);
            setDigits('');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [result]);

  useEffect(() => {
    if (members) {
      setCountdown(10);
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setMembers(null);
            setDigits('');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [members]);

  useEffect(() => {
    if (staffCalled) {
      const interval = setInterval(() => {
        setStaffCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setStaffCalled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [staffCalled]);

  const handleDigit = useCallback((num) => {
    if (loading || result || members) return;
    setDigits(prev => {
      if (prev.length >= 4) return prev;
      playKeySound();
      return prev + num;
    });
  }, [loading, result, members]);

  const handleBackspace = useCallback(() => {
    if (loading || result || members) return;
    playKeySound();
    setDigits(prev => prev.slice(0, -1));
  }, [loading, result, members]);

  const handleSubmit = useCallback(async () => {
    if (digits.length !== 4 || loading || result || members) return;
    setLoading(true);
    try {
      const res = await kioskAttendCheck({ clubID, phoneLast4: digits });
      const data = res.data;
      if (data.result === 1) {
        setResult({ type: 'success', playerName: data.playerName, memberships: data.memberships || [], locker: data.locker || null });
      } else if (data.result === 2) {
        setMembers(data.members);
      } else {
        setResult({ type: 'fail', message: data.error || '회원 정보를 찾을 수 없습니다.' });
      }
    } catch (e) {
      setResult({ type: 'fail', message: '오류가 발생했습니다. 다시 시도해주세요.' });
    }
    setLoading(false);
  }, [digits, clubID, loading, result, members]);

  const handleSelectMember = useCallback(async (playerID) => {
    setLoading(true);
    setMembers(null);
    try {
      const res = await kioskAttendSelect({ clubID, playerID });
      const data = res.data;
      if (data.result === 1) {
        setResult({ type: 'success', playerName: data.playerName, memberships: data.memberships || [], locker: data.locker || null });
      } else {
        setResult({ type: 'fail', message: data.error || '출석 처리에 실패했습니다.' });
      }
    } catch (e) {
      setResult({ type: 'fail', message: '오류가 발생했습니다. 다시 시도해주세요.' });
    }
    setLoading(false);
  }, [clubID]);

  const handleStaffCall = useCallback(async () => {
    if (staffCalled) return;
    setStaffCalled(true);
    setStaffCountdown(5);
    playStaffCallSound();
    try {
      await kioskStaffCall({ clubID });
    } catch (e) {}
  }, [clubID, staffCalled]);

  const handleCancelSelect = useCallback(() => {
    setMembers(null);
    setDigits('');
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (members) return;
      if (e.key >= '0' && e.key <= '9') {
        handleDigit(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      } else if (e.key === 'Enter') {
        handleSubmit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleDigit, handleBackspace, handleSubmit, members]);

  return (
    <PageWrapper>
      <FullscreenButton onClick={toggleFullscreen}>
        {isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
      </FullscreenButton>
      <LeftSection>
        <LeftContent>
          <GymName>{clubName || ''}</GymName>
          <Instruction>
            휴대폰 번호 뒤 4자리를 입력하고<br />
            출석 버튼을 눌러주세요.
          </Instruction>
        </LeftContent>
        <LeftBottom>
          <StaffCallButton onClick={handleStaffCall} disabled={staffCalled}>
            {staffCalled ? '호출 완료' : '직원 호출'}
          </StaffCallButton>
          <LogoImg src="/trepfit_logo.png" alt="TREPFIT" />
        </LeftBottom>
      </LeftSection>

      <RightSection>
        <NumpadCard>
          <PhoneDisplay>
            <PhoneLabel>휴대폰 번호 뒤 4자리</PhoneLabel>
            <PhoneNumber>
              {[0, 1, 2, 3].map(i => (
                <PhoneDigit key={i} filled={!!digits[i]}>
                  {digits[i] || '\u2015'}
                </PhoneDigit>
              ))}
            </PhoneNumber>
          </PhoneDisplay>

          <NumpadGrid>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
              <GridCell key={num} onClick={() => handleDigit(String(num))}>
                {num}
              </GridCell>
            ))}
            <GridCell style={{ cursor: 'default' }} />
            <GridCell onClick={() => handleDigit('0')}>0</GridCell>
            <BackspaceCell onClick={handleBackspace}>
              <MdBackspace />
            </BackspaceCell>
          </NumpadGrid>
          <SubmitButton
            disabled={digits.length !== 4 || loading}
            onClick={handleSubmit}
          >
            {loading ? '확인 중...' : '출석'}
          </SubmitButton>
        </NumpadCard>
      </RightSection>

      {members && (
        <Overlay>
          <SelectCard>
            <SelectTitle>회원을 선택해주세요</SelectTitle>
            <SelectSubtitle>동일한 번호의 회원이 여러명입니다. ({countdown}초)</SelectSubtitle>
            <MemberList>
              {members.map(m => (
                <MemberItem key={m.playerID} onClick={() => handleSelectMember(m.playerID)}>
                  <MemberName>{m.name}</MemberName>
                  <MemberPhone>{formatPhone(m.phone)}</MemberPhone>
                </MemberItem>
              ))}
            </MemberList>
            <CancelButton onClick={handleCancelSelect}>취소</CancelButton>
          </SelectCard>
        </Overlay>
      )}

      {result && (
        <Overlay onClick={() => { setResult(null); setDigits(''); }}>
          <ResultCard onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => { setResult(null); setDigits(''); }}>{'\u2715'}</CloseButton>
            {result.type === 'success' ? (
              <ResultHeader>
                <ResultIcon success>
                  {'\u2713'}
                </ResultIcon>
                <ResultTitle success>
                  {result.playerName}님 환영합니다!
                </ResultTitle>
              </ResultHeader>
            ) : (
              <>
                <ResultTitle>
                  출석 실패
                </ResultTitle>
                <ResultMessage>{result.message}</ResultMessage>
              </>
            )}
            {result.type === 'success' && (
              <>
                {(() => {
                  const days = calcRemainingDays(result.memberships);
                  return days !== null ? (
                    <RemainingDaysWrap>
                      <RemainingDaysNumber>{days}</RemainingDaysNumber>
                      <RemainingDaysLabel>일 남음</RemainingDaysLabel>
                    </RemainingDaysWrap>
                  ) : null;
                })()}
                <InfoSection>
                  <InfoLabel>회원권</InfoLabel>
                  {(() => {
                    const now = Math.floor(Date.now() / 1000);
                    const active = result.memberships && result.memberships.filter(m => m.startTime <= now && m.endTime >= now);
                    const rep = active && active.length > 0 ? active[0] : (result.memberships && result.memberships[0]);
                    return rep ? (
                      <InfoCard>
                        <InfoName>{rep.title}</InfoName>
                        <InfoDate>
                          {formatDate(rep.startTime)} ~ {formatDate(rep.endTime)}
                        </InfoDate>
                      </InfoCard>
                    ) : (
                      <InfoEmpty>등록된 회원권이 없습니다.</InfoEmpty>
                    );
                  })()}
                  {result.locker && (
                    <>
                      <InfoLabel>락커</InfoLabel>
                      <InfoCard>
                        <InfoName>{result.locker.name} {result.locker.lockerSN > 0 ? `#${result.locker.lockerSN}` : ''}</InfoName>
                        <InfoDate>{formatDate(result.locker.startTime)} ~ {formatDate(result.locker.endTime)}</InfoDate>
                      </InfoCard>
                    </>
                  )}
                </InfoSection>
              </>
            )}
            <CountdownText>{resultCountdown}초 후 자동으로 닫힙니다</CountdownText>
          </ResultCard>
        </Overlay>
      )}

      {staffCalled && (
        <Overlay onClick={() => setStaffCalled(false)}>
          <ResultCard onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setStaffCalled(false)}>{'\u2715'}</CloseButton>
            <ResultTitle style={{ color: '#3B82F6' }}>직원을 호출하였습니다</ResultTitle>
            <ResultMessage>잠시만 기다려주세요.</ResultMessage>
            <CountdownText>{staffCountdown}초 후 자동으로 닫힙니다</CountdownText>
          </ResultCard>
        </Overlay>
      )}
    </PageWrapper>
  );
};

export default KioskAttendCheckPage;
